import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, CheckCircle, ArrowLeft, KeyRound, Clock } from 'lucide-react';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { AuthService } from '../services/authService';
import { logger } from '../utils/logger';

type FormStatus = 'idle' | 'submitting' | 'success' | 'rate_limited';

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [rateLimitMessage, setRateLimitMessage] = useState('');

  const validateEmail = (value: string): boolean => {
    if (!value) {
      setEmailError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      return;
    }

    setStatus('submitting');

    try {
      await AuthService.requestPasswordReset(email.trim());
      // Show success message
      setStatus('success');
    } catch (error: unknown) {
      logger.error('Password reset request failed', { error });
      // Handle rate limiting explicitly
      if (axios.isAxiosError(error) && (error.response?.status === 429 || error.status === 429)) {
        const errorData = error.response?.data as Record<string, string> | undefined;
        setRateLimitMessage(errorData?.message || 'Too many requests. Please try again later.');
        setStatus('rate_limited');
      } else {
        // Still show success to prevent email enumeration for other errors
        setStatus('success');
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar showAuthButton={false} />

      <main className="flex-1 px-6 lg:px-8 py-12 pt-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Information */}
            <div className="hidden lg:block space-y-8">
              <div className="space-y-4">
                <h1
                  className="text-4xl font-bold leading-tight"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {status === 'success'
                    ? 'Check your email'
                    : status === 'rate_limited'
                      ? 'Slow down'
                      : 'Forgot your password?'}
                </h1>
                <p
                  className="text-lg leading-relaxed"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {status === 'success'
                    ? "We've sent you a link to reset your password. The link will expire in 1 hour."
                    : status === 'rate_limited'
                      ? "You've made too many requests. Please wait a few minutes before trying again."
                      : "No worries! Enter your email address and we'll send you a link to reset your password."}
                </p>
              </div>

              {/* Help Tips */}
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'var(--color-accent-teal)' }}
                  >
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      Check your inbox
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      The reset link will arrive within a few minutes
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'var(--color-accent-blue)' }}
                  >
                    <KeyRound className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      Check spam folder
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      Sometimes reset emails end up in spam or junk
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Form Card */}
            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <div
                className="bg-white dark:bg-gray-800 rounded-xl p-8"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border-default)',
                }}
              >
                {/* Icon */}
                <div className="flex justify-center mb-6">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{
                      backgroundColor:
                        status === 'success'
                          ? 'var(--color-accent-green)'
                          : status === 'rate_limited'
                            ? '#f59e0b'
                            : 'var(--color-accent-teal)',
                    }}
                  >
                    {status === 'success' ? (
                      <CheckCircle className="w-10 h-10 text-white" />
                    ) : status === 'submitting' ? (
                      <Loader2 className="w-10 h-10 text-white animate-spin" />
                    ) : status === 'rate_limited' ? (
                      <Clock className="w-10 h-10 text-white" />
                    ) : (
                      <Mail className="w-10 h-10 text-white" />
                    )}
                  </div>
                </div>

                {/* Header */}
                <div className="text-center mb-6">
                  <h2
                    className="text-2xl font-semibold mb-2"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {status === 'success'
                      ? 'Email sent!'
                      : status === 'rate_limited'
                        ? 'Too many requests'
                        : 'Reset your password'}
                  </h2>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {status === 'success'
                      ? 'If an account with that email exists, you will receive a password reset link shortly.'
                      : status === 'rate_limited'
                        ? rateLimitMessage
                        : "Enter your email address and we'll send you a reset link."}
                  </p>
                </div>

                {/* Form */}
                {(status === 'idle' || status === 'submitting') && (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Email Input */}
                    <div className="space-y-2">
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        Email Address{' '}
                        <span style={{ color: 'var(--color-error, #ef4444)' }}>*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) validateEmail(e.target.value);
                        }}
                        onBlur={() => email && validateEmail(email)}
                        placeholder="Enter your email address"
                        disabled={status === 'submitting'}
                        className="w-full px-4 py-3 rounded-lg text-sm transition-colors"
                        style={{
                          backgroundColor: 'var(--color-bg-tertiary)',
                          border: emailError
                            ? '1px solid var(--color-error, #ef4444)'
                            : '1px solid var(--color-border-default)',
                          color: 'var(--color-text-primary)',
                          opacity: status === 'submitting' ? 0.7 : 1,
                        }}
                        autoComplete="email"
                        autoFocus
                      />
                      {emailError && (
                        <p className="text-xs" style={{ color: 'var(--color-error, #ef4444)' }}>
                          {emailError}
                        </p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base font-medium"
                    >
                      {status === 'submitting' ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Reset Link'
                      )}
                    </button>
                  </form>
                )}

                {/* Success State */}
                {status === 'success' && (
                  <div className="space-y-4">
                    <div
                      className="p-4 rounded-lg border"
                      style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        borderColor: 'rgba(16, 185, 129, 0.3)',
                      }}
                    >
                      <p className="text-sm" style={{ color: '#10b981' }}>
                        Check your email for a link to reset your password. If it doesn't appear
                        within a few minutes, check your spam folder.
                      </p>
                    </div>

                    <button
                      onClick={() => {
                        setStatus('idle');
                        setEmail('');
                      }}
                      className="w-full py-3 px-4 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)',
                        color: 'var(--color-text-primary)',
                        border: '1px solid var(--color-border-default)',
                      }}
                    >
                      Send another email
                    </button>
                  </div>
                )}

                {/* Rate Limited State */}
                {status === 'rate_limited' && (
                  <div className="space-y-4">
                    <div
                      className="p-4 rounded-lg border"
                      style={{
                        backgroundColor: 'rgba(245, 158, 11, 0.1)',
                        borderColor: 'rgba(245, 158, 11, 0.3)',
                      }}
                    >
                      <p className="text-sm" style={{ color: '#f59e0b' }}>
                        {rateLimitMessage}
                      </p>
                    </div>

                    <button
                      onClick={() => {
                        setStatus('idle');
                      }}
                      className="w-full py-3 px-4 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: 'var(--color-bg-tertiary)',
                        color: 'var(--color-text-primary)',
                        border: '1px solid var(--color-border-default)',
                      }}
                    >
                      Try again
                    </button>
                  </div>
                )}

                {/* Back to Sign In Link */}
                <div
                  className="mt-6 pt-6 border-t"
                  style={{ borderColor: 'var(--color-border-default)' }}
                >
                  <Link
                    to="/signin"
                    className="flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                    style={{ color: 'var(--color-accent-teal)' }}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </Link>
                </div>

                {/* Help Text */}
                <p
                  className="text-xs text-center mt-6"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  Having trouble? Contact us at support@autar.ai
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;
